// Pestaña "Consentimientos" (Rediseño mayor, cierre del hallazgo de la
// auditoría de cumplimiento: "el sistema no distingue tipo de
// consentimiento"). Antes de esto los 7 documentos de consentimiento (024 +
// los 6 nuevos de lib/consentimientos.ts) solo se veían como archivos
// sueltos, sin distinción, en DocumentosTab — acá se listan agrupados por
// subtipo real (Consent.category[1], ver formulario/extractors.ts) y se
// puede generar cualquiera de los 7 sin salir de la ficha. Mismo patrón de
// refresh manual que DocumentosTab.tsx (useSearchResources no expone un
// refetch utilizable acá).
import type { MedplumClient } from '@medplum/core';
import { useMedplum } from '@medplum/react-hooks';
import type { Consent, DocumentReference } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { notify } from '@/components/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CONSENTIMIENTOS, SISTEMA_EXTENSION_REVOCA, SISTEMA_SUBTIPO_CONSENTIMIENTO, construirRevocacion } from '@/lib/consentimientos';
import { CODIGO_REVOCATORIA_024 } from '../formulario/RevocatoriaConsentimiento024';
import { TablaVacia } from './TablaVacia';

function subtipoDe(consent: Consent): string | undefined {
  return consent.category?.flatMap((c) => c.coding ?? []).find((c) => c.system === SISTEMA_SUBTIPO_CONSENTIMIENTO)?.code;
}

/** Mismo patrón que verDocumento() de DocumentosTab.tsx, pero partiendo de un `Consent` — resuelve su `sourceReference` antes de bajar el adjunto. */
async function verConsentimiento(medplum: MedplumClient, consent: Consent, mensajeSinAdjunto: string): Promise<void> {
  const ref = consent.sourceReference;
  if (!ref?.reference) {
    notify({ color: 'red', message: mensajeSinAdjunto });
    return;
  }
  try {
    const docRef = await medplum.readReference<DocumentReference>({ reference: ref.reference });
    const url = docRef.content?.[0]?.attachment?.url;
    if (!url) {
      notify({ color: 'red', message: mensajeSinAdjunto });
      return;
    }
    const blob = await medplum.download(url);
    const objectUrl = URL.createObjectURL(blob);
    window.open(objectUrl, '_blank');
  } catch (err) {
    notify({ color: 'red', message: err instanceof Error ? err.message : String(err) });
  }
}

/** Ids de los `Consent` que ya tienen una revocación (un `Consent` inactivo cuya extensión `revoca` los apunta) — el original nunca se edita. */
function idsRevocados(lista: readonly Consent[]): Set<string> {
  const ids = new Set<string>();
  for (const c of lista) {
    for (const e of c.extension ?? []) {
      const ref = e.url === SISTEMA_EXTENSION_REVOCA ? e.valueReference?.reference : undefined;
      if (ref?.startsWith('Consent/')) ids.add(ref.slice('Consent/'.length));
    }
  }
  return ids;
}

async function revocarConsentimiento(
  medplum: MedplumClient,
  consent: Consent,
  confirmarMsg: string,
  mensajeOk: string,
  refrescar: () => void,
): Promise<void> {
  if (!window.confirm(confirmarMsg)) return;
  try {
    await construirRevocacion(medplum, consent);
    notify({ color: 'green', message: mensajeOk });
    refrescar();
  } catch (err) {
    notify({ color: 'red', message: err instanceof Error ? err.message : String(err) });
  }
}

function useConsentimientosConRefresh(patientId: string) {
  const medplum = useMedplum();
  const [version, setVersion] = useState(0);
  const [consentimientos, setConsentimientos] = useState<Consent[] | undefined>(undefined);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    medplum
      .searchResources('Consent', { patient: `Patient/${patientId}`, _sort: '-date', _count: 100 })
      .then((r) => {
        if (!cancelado) {
          setConsentimientos(r as Consent[]);
          setCargando(false);
        }
      })
      .catch(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [medplum, patientId, version]);

  return [consentimientos, cargando, () => setVersion((v) => v + 1)] as const;
}

export function ConsentimientosTab(): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { id } = useParams() as { id: string };
  const [consentimientos, cargando, refrescar] = useConsentimientosConRefresh(id);

  const lista = consentimientos ?? [];
  const revocados = idsRevocados(lista);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <h5 className="text-sm font-semibold">{t('consentimientosTab.generarNuevo')}</h5>
        <div className="flex flex-wrap gap-2">
          {CONSENTIMIENTOS.map(({ codigo }) => (
            <Button key={codigo} asChild size="sm" variant="outline">
              <Link to={`/${id}/formulario/${codigo}`}>{t(`hub.formulario.${codigo}`)}</Link>
            </Button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <h5 className="text-sm font-semibold">{t('consentimientosTab.listaTitulo')}</h5>
        {cargando ? (
          <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>
        ) : lista.length === 0 ? (
          <TablaVacia mensaje={t('fichaPaciente.sinDatos')} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('fichaPaciente.columnas.fecha')}</TableHead>
                <TableHead>{t('fichaPaciente.columnas.categoria')}</TableHead>
                <TableHead>{t('fichaPaciente.columnas.estado')}</TableHead>
                <TableHead>{t('fichaPaciente.columnas.documento')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lista.map((c) => {
                const subtipo = subtipoDe(c);
                const etiquetaSubtipo = subtipo ? t(`consentimientosTab.subtipos.${subtipo}`, subtipo) : '—';
                const yaRevocado = !!c.id && revocados.has(c.id);
                const etiquetaEstado = yaRevocado ? t('consentimientosTab.estado.inactive') : c.status ? t(`consentimientosTab.estado.${c.status}`, c.status) : '—';
                return (
                  <TableRow key={c.id}>
                    <TableCell>{c.dateTime ? new Date(c.dateTime).toLocaleString('es-EC') : '—'}</TableCell>
                    <TableCell>{etiquetaSubtipo}</TableCell>
                    <TableCell>{etiquetaEstado}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          className="text-sm text-hce-pine underline-offset-2 hover:underline"
                          onClick={() => void verConsentimiento(medplum, c, t('fichaPaciente.documentoSinAdjunto'))}
                        >
                          {t('formulario.firma.verImprimir')}
                        </button>
                        {c.status === 'active' && !yaRevocado && subtipo === 'consent-024' && (
                          // El 024 se revoca con su propio documento (bloque E del papel: datos, firma y PDF), no con un clic.
                          <Link className="text-sm text-destructive underline-offset-2 hover:underline" to={`/${id}/formulario/${CODIGO_REVOCATORIA_024}?consent=${c.id}`}>
                            {t('consentimientosTab.revocar')}
                          </Link>
                        )}
                        {c.status === 'active' && !yaRevocado && subtipo !== 'consent-024' && (
                          <button
                            type="button"
                            className="text-sm text-destructive underline-offset-2 hover:underline"
                            onClick={() =>
                              void revocarConsentimiento(
                                medplum,
                                c,
                                t('consentimientosTab.revocarConfirmar'),
                                t('consentimientosTab.revocado'),
                                refrescar,
                              )
                            }
                          >
                            {t('consentimientosTab.revocar')}
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
